/* Runtime constants shared by the AI action flows: ratio catalogs, model
   limits, droppable ids and the option lists the panels render. */
import { ImageCategory } from "../../types"
import type { SlideMotion } from "../KenBurnsPlayer"
import type { FrameRatio, RefRole, VeoDuration, VeoRatio } from "./types"

// ── Droppable ids ────────────────────────────────────────────────────────

/** Source drop zone shown by single-source actions until a source is picked. */
export const SOURCE_DROP_ID = "ai-source:drop"
/** The "merge another image" slot in the images row. */
export const AD_REF_DROP_ID = "ai-ad-ref:drop"
/** The hero Product tile — dropping a drawer image here swaps the source. */
export const HERO_DROP_ID = "ai-hero:drop"
/** The card-update editor's reference-image slot. */
export const CARD_REF_DROP_ID = "ai-card-ref:drop"
/** The slideshow timeline's empty "drop here" slot. */
export const SLIDESHOW_DROP_ID = "slideshow-flow:drop"

// ── Veo ──────────────────────────────────────────────────────────────────

// Every Veo-supported ratio. We submit one generation job per ratio in
// parallel so the user gets coverage for both portrait (Stories/Reels)
// and landscape placements in a single click — no dropdown to fiddle
// with. 4:5 and 1.91:1 aren't supported by Veo natively; users can crop
// the 16:9 output for those placements during launch.
export const VEO_RATIOS: { ratio: VeoRatio; label: string }[] = [
  { ratio: "9:16", label: "9:16" },
  { ratio: "16:9", label: "16:9" },
]

// Veo's reference-to-video mode takes at most three images TOTAL and the hero
// shot occupies one of them, so only two extra references ever reach the
// model. Mirrors MAX_MOTION_REFERENCES in ad-forge.service.ts — the server
// truncates too, but silently, so the panel caps and labels it up front.
export const MAX_MOTION_REFERENCES = 3

// reference_to_video only supports 8-second clips. The server forces this
// whenever references are attached; the selector mirrors it so the user isn't
// shown a length that will be ignored.
export const REFERENCE_MODE_DURATION: VeoDuration = "8"

// ── Composed frames ──────────────────────────────────────────────────────

export const FRAME_RATIOS: FrameRatio[] = ["1:1", "4:5", "9:16", "16:9"]

export const FRAME_SLOTS: Record<
  FrameRatio,
  "square" | "portrait" | "stories" | "landscape"
> = {
  "1:1": "square",
  "4:5": "portrait",
  "9:16": "stories",
  "16:9": "landscape",
}

// ── Reference roles ──────────────────────────────────────────────────────

export const REF_ROLE_LABELS: Record<RefRole, string> = {
  product: "Product angle",
  packshot: "Packshot",
  lifestyle: "Lifestyle",
  onModel: "On model",
  detail: "Detail",
  background: "Background",
  style: "Style ref",
}

/** Feed classification → the role shown in the reference dropdown. */
export const CATEGORY_TO_ROLE: Partial<Record<ImageCategory, RefRole>> = {
  [ImageCategory.LAYDOWN]: "packshot",
  [ImageCategory.LIFESTYLE]: "lifestyle",
  [ImageCategory.ON_BODY]: "onModel",
  [ImageCategory.PRODUCT_DETAIL]: "detail",
  [ImageCategory.FABRIC_DETAIL]: "detail",
}

// ── Motion effect chips ──────────────────────────────────────────────────

// Each chip maps to a phrase stitched into the Veo motion prompt.
export const EFFECT_OPTIONS: { key: string; label: string; phrase: string }[] =
  [
    {
      key: "cinematic",
      label: "Cinematic",
      phrase: "cinematic camera movement with smooth, graceful motion",
    },
    {
      key: "zoom",
      label: "Product zoom",
      phrase: "a slow, elegant push-in emphasizing the product",
    },
    {
      key: "parallax",
      label: "Parallax depth",
      phrase: "subtle parallax depth between the product and the background",
    },
    {
      key: "studio",
      label: "Studio lighting",
      phrase: "polished studio lighting with soft moving highlights",
    },
    {
      key: "energetic",
      label: "Vibrant & energetic",
      phrase: "vibrant colors and punchy, energetic motion",
    },
    {
      key: "elegant",
      label: "Minimal & elegant",
      phrase: "a minimal, elegant, premium feel with restrained motion",
    },
    {
      key: "glow",
      label: "Soft glow",
      phrase: "a soft glow and gentle drifting light rays",
    },
    {
      key: "float",
      label: "Floating product",
      phrase: "the product gently floating with a subtle rotation",
    },
  ]

// ── Slideshow ────────────────────────────────────────────────────────────

// Per-slide motion labels — `SlideMotion` itself is exported by
// KenBurnsPlayer so the renderer and the panel stay in lock-step.
export const MOTION_LABELS: Record<SlideMotion, string> = {
  ltr: "Left → Right",
  rtl: "Right → Left",
  ttb: "Top → Bottom",
  btt: "Bottom → Top",
  "zoom-in": "Zoom in",
  "zoom-out": "Zoom out",
}

// Compact arrow/zoom badge per motion direction, so the timeline doesn't need
// full text on every card.
export const MOTION_ICON: Record<SlideMotion, string> = {
  ltr: "→",
  rtl: "←",
  ttb: "↓",
  btt: "↑",
  "zoom-in": "⊕",
  "zoom-out": "⊖",
}
